import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { http } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import PresupuestoForm from './PresupuestoForm';
import PresupuestoDetalleModal from './PresupuestoDetalleModal';
import { buildDisplayNameFromUser, buildDisplayNameFromMaybeObject, isLikelyUsername } from '../../lib/displayName';
import { 
  Plus, 
  Eye, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  FileText, 
  AlertCircle,
  Search,
  X,
  PackageCheck,
  Send,
  ShieldCheck,
  RotateCcw
} from 'lucide-react';

const PresupuestosList = () => {
    const [presupuestos, setPresupuestos] = useState([]);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const { toast } = useToast();
    const [loading, setLoading] = useState(true);
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [presupuestoEditando, setPresupuestoEditando] = useState(null);
    const [modoLectura, setModoLectura] = useState(false);
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [busqueda, setBusqueda] = useState('');
    const [tecnicos, setTecnicos] = useState([]);

    const loadPresupuestos = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await http.get('/presupuestos');
            
            if (!response.data) {
                throw new Error('No se recibieron datos del servidor');
            }
            
            const presupuestosData = Array.isArray(response.data) ? response.data : [];
            setPresupuestos(presupuestosData);
        } catch (error) {
            console.error('Error al cargar presupuestos:', error);
            setError(error.message);
            toast({
                title: "Error al cargar presupuestos",
                description: error.response?.data?.detail || error.message,
                variant: "destructive",
            });
            setPresupuestos([]);
        } finally {
            setLoading(false);
        }
    };

    const loadTecnicos = useCallback(async () => {
        try {
            // Cargar todos los usuarios activos (técnicos y admins) para poder resolver nombres
            const response = await http.get('/usuarios', {
                params: { activo: true }
            });
            const data = Array.isArray(response.data) ? response.data : [];
            // Filtrar solo técnicos y admins (quienes pueden crear presupuestos)
            const usuariosRelevantes = data.filter(u => 
                u.rol === 'tecnico' || u.rol === 'admin'
            );
            setTecnicos(usuariosRelevantes);
        } catch (error) {
            console.error('Error al cargar técnicos:', error);
            // Si falla, intentar sin filtros de permisos (en caso de ser técnico sin acceso a /usuarios)
            setTecnicos([]);
        }
    }, []);

    useEffect(() => {
        loadPresupuestos();
        loadTecnicos();
    }, [loadTecnicos]);

    const handleEstadoChange = async (presupuestoId, nuevoEstado) => {
        try {
            await http.put(`/presupuestos/${presupuestoId}/estado`, {
                estado: nuevoEstado
            });
            toast({
                title: "Estado actualizado",
                description: "Estado actualizado correctamente",
            });
            loadPresupuestos();
        } catch (error) {
            console.error('Error al actualizar estado:', error);
            toast({
                title: "Error al actualizar estado",
                description: error.response?.data?.detail || error.message,
                variant: "destructive",
            });
        }
    };

    const handleEliminar = async (presupuestoId) => {
        if (!window.confirm('¿Está seguro de que desea eliminar este presupuesto?')) {
            return;
        }

        try {
            await http.delete(`/presupuestos/${presupuestoId}`);
            toast({
                title: "Presupuesto eliminado",
                description: "Presupuesto eliminado correctamente",
            });
            loadPresupuestos();
        } catch (error) {
            console.error('Error al eliminar presupuesto:', error);
            toast({
                title: "Error al eliminar presupuesto",
                description: error.response?.data?.detail || error.message,
                variant: "destructive",
            });
        }
    };

    const handleNuevoPresupuesto = () => {
        setPresupuestoEditando(null);
        setModoLectura(false);
        setMostrarFormulario(true);
    };

    const handleEditarPresupuesto = (presupuesto) => {
        setPresupuestoEditando(presupuesto);
        setModoLectura(false);
        setMostrarFormulario(true);
    };

    const handleVerPresupuesto = (presupuesto) => {
        setPresupuestoEditando(presupuesto);
        setModoLectura(true);
        setMostrarFormulario(true);
    };

    const handleCerrarFormulario = () => {
        setMostrarFormulario(false);
        setPresupuestoEditando(null);
        setModoLectura(false);
    };

    const handleGuardarPresupuesto = () => {
        loadPresupuestos();
        setMostrarFormulario(false);
        setPresupuestoEditando(null);
    };

    const puedeEditar = (presupuesto) => {
        if (user.rol === 'admin') return true;
        if (user.rol === 'tecnico' && 
            presupuesto.usuario_creador_id === user._id && 
            presupuesto.estado === 'borrador') return true;
        return false;
    };

    const resolveTecnicoNombre = useCallback((presupuesto) => {
        if (!presupuesto) return '';

        if (presupuesto.tecnico_id && tecnicos.length) {
            const matchById = tecnicos.find((tecnico) => (tecnico._id || tecnico.id) === presupuesto.tecnico_id);
            const matchDisplay = buildDisplayNameFromUser(matchById);
            if (matchDisplay) return matchDisplay;
        }

        const candidateSources = [
            presupuesto.tecnico_nombre,
            presupuesto.nombre_tecnico,
            presupuesto.tecnico,
            presupuesto.usuario_creador_nombre,
            presupuesto.usuario_creador
        ];

        const candidateStrings = candidateSources
            .map((value) => buildDisplayNameFromMaybeObject(value))
            .filter(Boolean);

        const nonUsername = candidateStrings.find((value) => !isLikelyUsername(value));
        if (nonUsername) return nonUsername;

        if (candidateStrings.length && tecnicos.length) {
            const fallback = candidateStrings[0];
            if (isLikelyUsername(fallback)) {
                const matchByUsername = tecnicos.find((tecnico) =>
                    tecnico.username && fallback && tecnico.username.toLowerCase() === fallback.toLowerCase()
                );
                const matchDisplay = buildDisplayNameFromUser(matchByUsername);
                if (matchDisplay) return matchDisplay;
            }
            return fallback;
        }

        if (presupuesto.usuario_creador) {
            const creatorDisplay = buildDisplayNameFromUser(presupuesto.usuario_creador);
            if (creatorDisplay) return creatorDisplay;
        }

        return '';
    }, [tecnicos]);

    const puedeEliminar = (presupuesto) => {
        if (user.rol === 'admin') return true;
        if (user.rol === 'tecnico' && 
            presupuesto.usuario_creador_id === user._id && 
            presupuesto.estado === 'borrador') return true;
        return false;
    };

    // Filtrado combinado: estado + búsqueda libre  (debe ir DESPUÉS de resolveTecnicoNombre)
    const presupuestosFiltrados = useMemo(() => {
        let lista = presupuestos;
        if (filtroEstado !== 'todos') {
            lista = lista.filter(p => p.estado === filtroEstado);
        }
        if (busqueda.trim()) {
            const q = busqueda.toLowerCase();
            lista = lista.filter(p => {
                const tecNombre = resolveTecnicoNombre(p).toLowerCase();
                return (
                    (p.cliente_nombre || '').toLowerCase().includes(q) ||
                    (p.sucursal_nombre || '').toLowerCase().includes(q) ||
                    (p.descripcion || '').toLowerCase().includes(q) ||
                    (p.detalle || '').toLowerCase().includes(q) ||
                    (p.equipo_nombre || '').toLowerCase().includes(q) ||
                    getEstadoTexto(p.estado).toLowerCase().includes(q) ||
                    tecNombre.includes(q)
                );
            });
        }
        return lista;
    }, [presupuestos, filtroEstado, busqueda, resolveTecnicoNombre]); // eslint-disable-line

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'borrador':             return 'bg-gray-100 text-gray-700 border border-gray-200';
            case 'verificado':           return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
            case 'pendiente_aprobacion': return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
            case 'aprobado':             return 'bg-green-100 text-green-700 border border-green-200';
            case 'rechazado':            return 'bg-red-100 text-red-700 border border-red-200';
            case 'entregado':            return 'bg-purple-100 text-purple-700 border border-purple-200';
            default:                     return 'bg-gray-100 text-gray-700 border border-gray-200';
        }
    };

    const getEstadoIcon = (estado) => {
        switch (estado) {
            case 'borrador':             return <FileText className="w-3.5 h-3.5" />;
            case 'verificado':           return <ShieldCheck className="w-3.5 h-3.5" />;
            case 'pendiente_aprobacion': return <Clock className="w-3.5 h-3.5" />;
            case 'aprobado':             return <CheckCircle className="w-3.5 h-3.5" />;
            case 'rechazado':            return <XCircle className="w-3.5 h-3.5" />;
            case 'entregado':            return <PackageCheck className="w-3.5 h-3.5" />;
            default:                     return <AlertCircle className="w-3.5 h-3.5" />;
        }
    };

    const getEstadoTexto = (estado) => {
        switch (estado) {
            case 'borrador':             return 'Borrador';
            case 'verificado':           return 'Verificado';
            case 'pendiente_aprobacion': return 'Pendiente';
            case 'aprobado':             return 'Aprobado';
            case 'rechazado':            return 'Rechazado';
            case 'entregado':            return 'Entregado';
            default:                     return estado;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    // Estadísticas
    const estadisticas = {
        total:                presupuestos.length,
        borrador:             presupuestos.filter(p => p.estado === 'borrador').length,
        verificado:           presupuestos.filter(p => p.estado === 'verificado').length,
        pendiente_aprobacion: presupuestos.filter(p => p.estado === 'pendiente_aprobacion').length,
        aprobado:             presupuestos.filter(p => p.estado === 'aprobado').length,
        rechazado:            presupuestos.filter(p => p.estado === 'rechazado').length,
        entregado:            presupuestos.filter(p => p.estado === 'entregado').length,
    };

    const ESTADOS_CONFIG = [
        { key: 'borrador',             label: 'Borrador',  color: 'text-gray-600',  bg: 'bg-gray-50',   border: 'border-gray-200',  icon: <FileText className="w-4 h-4" /> },
        { key: 'verificado',           label: 'Verificado', color: 'text-emerald-600', bg: 'bg-emerald-50',   border: 'border-emerald-200',  icon: <ShieldCheck className="w-4 h-4" /> },
        { key: 'pendiente_aprobacion', label: 'Pendiente',  color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: <Clock className="w-4 h-4" /> },
        { key: 'aprobado',             label: 'Aprobado',  color: 'text-green-600', bg: 'bg-green-50',  border: 'border-green-200', icon: <CheckCircle className="w-4 h-4" /> },
        { key: 'rechazado',            label: 'Rechazado', color: 'text-red-600',   bg: 'bg-red-50',    border: 'border-red-200',   icon: <XCircle className="w-4 h-4" /> },
        { key: 'entregado',            label: 'Entregado', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', icon: <PackageCheck className="w-4 h-4" /> },
    ];

    return (
        <>
            <div className="space-y-5">

                {/* ── Header ── */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Presupuestos</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {user.rol === 'admin'
                                ? 'Gestión y aprobación de presupuestos'
                                : 'Tus presupuestos enviados'}
                        </p>
                    </div>
                    {(user.rol === 'tecnico' || user.rol === 'admin') && (
                        <button
                            onClick={handleNuevoPresupuesto}
                            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium text-sm shadow-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Nuevo Presupuesto
                        </button>
                    )}
                </div>

                {/* ── Tarjetas de estado (clickeables para filtrar) ── */}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {/* Total */}
                    <button
                        onClick={() => setFiltroEstado('todos')}
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                            filtroEstado === 'todos'
                                ? 'bg-emerald-600 border-emerald-600 text-white shadow-md scale-105'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-300 hover:shadow-sm'
                        }`}
                    >
                        <span className="text-2xl font-bold leading-none">{estadisticas.total}</span>
                        <span className="text-xs mt-1 font-medium opacity-80">Todos</span>
                    </button>
                    {ESTADOS_CONFIG.map(cfg => (
                        <button
                            key={cfg.key}
                            onClick={() => setFiltroEstado(cfg.key)}
                            className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                                filtroEstado === cfg.key
                                    ? `${cfg.bg} ${cfg.border} ${cfg.color} shadow-md scale-105`
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:shadow-sm'
                            }`}
                        >
                            <span className={`text-2xl font-bold leading-none ${filtroEstado === cfg.key ? cfg.color : 'text-gray-800'}`}>
                                {estadisticas[cfg.key]}
                            </span>
                            <span className="text-xs mt-1 font-medium opacity-80">{cfg.label}</span>
                        </button>
                    ))}
                </div>

                {/* ── Barra de búsqueda ── */}
                <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            placeholder="Buscar por cliente, descripción, equipo, técnico..."
                            className="w-full pl-9 pr-8 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                        {busqueda && (
                            <button
                                onClick={() => setBusqueda('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                        {presupuestosFiltrados.length} resultado{presupuestosFiltrados.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* ── Lista de presupuestos ── */}
                {presupuestosFiltrados.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">
                            {busqueda ? 'Sin resultados para esa búsqueda' : filtroEstado === 'todos' ? 'Aún no hay presupuestos' : `Sin presupuestos en estado "${getEstadoTexto(filtroEstado)}"`}
                        </p>
                        {!busqueda && (user.rol === 'tecnico' || user.rol === 'admin') && (
                            <button onClick={handleNuevoPresupuesto} className="mt-3 text-emerald-600 hover:underline text-sm font-medium">
                                Crear el primero
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {presupuestosFiltrados.map(presupuesto => (
                            <div
                                key={presupuesto._id}
                                className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all"
                            >
                                {/* Fila principal */}
                                <div className="flex items-center gap-3 px-4 py-3">
                                    {/* Badge de estado */}
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${getEstadoColor(presupuesto.estado)}`}>
                                        {getEstadoIcon(presupuesto.estado)}
                                        {getEstadoTexto(presupuesto.estado)}
                                    </span>

                                    {/* Info central */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <span className="font-semibold text-gray-900 text-sm truncate">
                                                {presupuesto.cliente_nombre || 'Sin cliente'}
                                            </span>
                                            {presupuesto.sucursal_nombre && (
                                                <span className="text-xs text-gray-500 truncate">
                                                    · {presupuesto.sucursal_nombre}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600 truncate mt-0.5">
                                            {presupuesto.descripcion || 'Sin descripción'}
                                        </p>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                            {presupuesto.equipo_nombre && (
                                                <span className="truncate">📦 {presupuesto.equipo_nombre}</span>
                                            )}
                                            {resolveTecnicoNombre(presupuesto) && (
                                                <span className="truncate">👤 {resolveTecnicoNombre(presupuesto)}</span>
                                            )}
                                            {presupuesto.created_at && (
                                                <span className="shrink-0">
                                                    {new Date(presupuesto.created_at).toLocaleDateString('es-PY', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Acciones */}
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            onClick={() => handleVerPresupuesto(presupuesto)}
                                            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                                            title="Ver detalles"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        {puedeEditar(presupuesto) && (
                                            <button
                                                onClick={() => handleEditarPresupuesto(presupuesto)}
                                                className="p-1.5 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"
                                                title="Editar"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                        )}
                                        {puedeEliminar(presupuesto) && (
                                            <button
                                                onClick={() => handleEliminar(presupuesto._id)}
                                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* ── Barra de acciones de flujo (solo admin) ── */}
                                {user.rol === 'admin' && (() => {
                                    const s = presupuesto.estado;
                                    const acciones = [];

                                    if (s === 'borrador') {
                                        acciones.push(
                                            <button key="v" onClick={() => handleEstadoChange(presupuesto._id, 'verificado')}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200">
                                                <ShieldCheck className="w-3.5 h-3.5" /> Verificar
                                            </button>
                                        );
                                    }
                                    if (s === 'verificado') {
                                        acciones.push(
                                            <button key="s" onClick={() => handleEstadoChange(presupuesto._id, 'pendiente_aprobacion')}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200">
                                                <Send className="w-3.5 h-3.5" /> Enviar a aprobación
                                            </button>
                                        );
                                        acciones.push(
                                            <button key="rb" onClick={() => handleEstadoChange(presupuesto._id, 'borrador')}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200">
                                                <RotateCcw className="w-3.5 h-3.5" /> Volver a borrador
                                            </button>
                                        );
                                    }
                                    if (s === 'pendiente_aprobacion') {
                                        acciones.push(
                                            <button key="apr" onClick={() => handleEstadoChange(presupuesto._id, 'aprobado')}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200">
                                                <CheckCircle className="w-3.5 h-3.5" /> Aprobar
                                            </button>
                                        );
                                        acciones.push(
                                            <button key="rec" onClick={() => handleEstadoChange(presupuesto._id, 'rechazado')}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 border border-red-200">
                                                <XCircle className="w-3.5 h-3.5" /> Rechazar
                                            </button>
                                        );
                                    }
                                    if (s === 'aprobado') {
                                        acciones.push(
                                            <button key="ent" onClick={() => handleEstadoChange(presupuesto._id, 'entregado')}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200">
                                                <PackageCheck className="w-3.5 h-3.5" /> Marcar Entregado
                                            </button>
                                        );
                                    }

                                    if (acciones.length === 0) return null;
                                    return (
                                        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-t border-gray-100 bg-gray-50/60 rounded-b-xl">
                                            <span className="text-xs text-gray-400 mr-1">Acción:</span>
                                            {acciones}
                                        </div>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal formulario */}
            {mostrarFormulario && !modoLectura && (
                <PresupuestoForm
                    presupuesto={presupuestoEditando}
                    onClose={handleCerrarFormulario}
                    onSave={handleGuardarPresupuesto}
                />
            )}

            {/* Modal detalle */}
            {mostrarFormulario && modoLectura && (
                <PresupuestoDetalleModal
                    presupuesto={presupuestoEditando}
                    onClose={handleCerrarFormulario}
                    onEditar={(p) => {
                        setModoLectura(false);
                        setPresupuestoEditando(p);
                    }}
                />
            )}
        </>
    );
};

export default PresupuestosList;
